import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { AdminRole, AdminPermission } from '@hisob/types';
import { setApiAuthToken, setUnauthorizedHandler } from '../api/client.js';
import { loginAdmin, type AdminLoginRequest } from '../api/auth.js';

interface AdminUserSession {
  id: string;
  email: string;
  name: string;
  role: AdminRole;
  permissions: AdminPermission[];
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  admin: AdminUserSession | null;
  login: (credentials: AdminLoginRequest) => Promise<void>;
  logout: () => void;
  hasPermission: (permission: AdminPermission) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Define client-side RBAC mirror for instant UI checks (Backend is still source of truth)
const ROLE_PERMISSIONS: Record<AdminRole, readonly AdminPermission[]> = {
  super_admin: ['all'],
  support: [
    'devices:read',
    'commands:read',
    'commands:create',
    'commands:cancel',
    'snapshots:read',
    'snapshots:delete',
    'users:read',
  ],
  developer: [
    'config:read',
    'config:write',
    'features:read',
    'features:write',
    'commands:read',
    'devices:read',
    'audit:read',
  ],
  auditor: [
    'audit:read',
    'config:read',
    'features:read',
    'devices:read',
    'commands:read',
    'users:read',
  ],
};

const SESSION_STORAGE_KEY = 'hisob_admin_session';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [admin, setAdmin] = useState<AdminUserSession | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const logout = useCallback(() => {
    setApiAuthToken(null);
    setAdmin(null);
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  }, []);

  useEffect(() => {
    // 401 handler triggers logout
    setUnauthorizedHandler(() => {
      logout();
    });

    // Restore session from sessionStorage (memory-safe, cleared on tab close)
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.token && parsed.admin) {
          setApiAuthToken(parsed.token);
          setAdmin(parsed.admin);
        }
      } catch {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
      }
    }
    setIsLoading(false);
  }, [logout]);

  const login = async (credentials: AdminLoginRequest) => {
    setIsLoading(true);
    try {
      const res = await loginAdmin(credentials);
      setAdmin(res.admin);
      setApiAuthToken(res.access_token);
      sessionStorage.setItem(
        SESSION_STORAGE_KEY,
        JSON.stringify({
          token: res.access_token,
          admin: res.admin,
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const checkPermission = useCallback(
    (requiredPermission: AdminPermission): boolean => {
      if (!admin) return false;
      if (admin.role === 'super_admin') return true;

      const allowed = ROLE_PERMISSIONS[admin.role];
      if (!allowed) return false;
      return allowed.includes('all') || allowed.includes(requiredPermission);
    },
    [admin]
  );

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: Boolean(admin),
        isLoading,
        admin,
        login,
        logout,
        hasPermission: checkPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
